import { useState } from "react"
import { Link, useRoute } from "wouter"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import { LanguageSwitcher } from "@/components/LanguageSwitcher"
import { Logo } from "@/components/Logo"
import { ArealSelectorDropdown } from "@/components/layout/ArealSelectorDropdown"
import { useAdminAuth } from "@/hooks/useAdminAuth"
import { useAdminAuthStore } from "@/store/adminAuthStore"
import { ChangePasswordModal } from "@/components/admin/ChangePasswordModal"
import { LayoutDashboard, DollarSign, KeyRound, Settings, FileText, Map, Receipt, ServerCog } from "lucide-react"

interface NavItemProps {
    label: string
    to: string
    icon: React.ReactNode
    exact?: boolean
}

const NavItem = ({ label, to, icon, exact }: NavItemProps) => {
    // Inside the nested /admin context, useRoute matches against scoped paths.
    // ~/admin → "/", ~/admin/pricing → "/pricing", etc.
    const scopedPath = to === "~/admin" ? "/" : to.replace(/^~?\/admin/, "")
    const [isActive] = useRoute(exact ? scopedPath : `${scopedPath}/*?`)
    return (
        <Link
            to={to}
            className={cn(
                "flex items-center gap-2.5 rounded-2xl px-3 py-2.5 text-sm font-bold transition-all duration-200",
                isActive
                    ? "bg-charcoal text-white shadow-lg"
                    : "text-slate-500 hover:bg-slate-50 hover:text-slate-700",
            )}
        >
            <span className={cn("size-4 shrink-0", isActive ? "text-brand" : "text-slate-400")}>
                {icon}
            </span>
            {label}
        </Link>
    )
}

export const AdminLayout = ({ children }: { children: React.ReactNode }) => {
    const { t } = useTranslation()
    const { logout } = useAdminAuth()
    const isPlatformAdmin = useAdminAuthStore((s) => s.user?.role === "admin")
    const [changePasswordOpen, setChangePasswordOpen] = useState(false)

    return (
        <div className="min-h-screen flex bg-slate-50">
            {/* Sidebar */}
            <aside className="w-60 border-r border-slate-200 bg-white flex flex-col shrink-0">
                <div className="px-5 py-5 border-b border-slate-100">
                    <Logo variant="color" height={28} />
                </div>
                <div className="px-3 pt-3 pb-2 border-b border-slate-100">
                    <ArealSelectorDropdown />
                </div>
                <nav className="flex-1 px-3 py-4 space-y-1">
                    <NavItem
                        to="~/admin"
                        exact
                        label={t("layout.admin.navDashboard")}
                        icon={<LayoutDashboard className="size-4" />}
                    />
                    <NavItem
                        to="~/admin/spot-map"
                        label={t("layout.admin.navSpotMap")}
                        icon={<Map className="size-4" />}
                    />
                    <NavItem
                        to="~/admin/pricing"
                        label={t("layout.admin.navPricing")}
                        icon={<DollarSign className="size-4" />}
                    />
                    <NavItem
                        to="~/admin/access-codes"
                        label={t("layout.admin.navAccessCodes")}
                        icon={<KeyRound className="size-4" />}
                    />
                    <NavItem
                        to="~/admin/city-report"
                        label={t("layout.admin.navCityReport")}
                        icon={<FileText className="size-4" />}
                    />
                    <NavItem
                        to="~/admin/billing"
                        label={t("layout.admin.navBilling")}
                        icon={<Receipt className="size-4" />}
                    />
                    <NavItem
                        to="~/admin/settings"
                        label={t("layout.admin.navSettings")}
                        icon={<Settings className="size-4" />}
                    />
                    {isPlatformAdmin && (
                        <NavItem
                            to="~/admin/platform"
                            label={t("layout.admin.navPlatform")}
                            icon={<ServerCog className="size-4" />}
                        />
                    )}
                </nav>
            </aside>

            <ChangePasswordModal open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} />

            {/* Main content */}
            <div className="flex-1 flex flex-col min-w-0">
                <header className="border-b border-slate-200 bg-white/95 backdrop-blur-md px-6 py-4 sticky top-0 z-40 shadow-sm">
                    <div className="flex items-center justify-between">
                        <span className="font-black text-slate-900">{t("layout.admin.headerTitle")}</span>
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setChangePasswordOpen(true)}
                                className="text-xs font-black uppercase tracking-widest text-slate-400 hover:text-slate-600 transition-colors"
                            >
                                {t("admin.changePassword.trigger")}
                            </button>
                            <button
                                onClick={() => logout()}
                                className="text-xs font-black uppercase tracking-widest text-slate-400 hover:text-slate-600 transition-colors"
                            >
                                {t("admin.dashboard.signOut")}
                            </button>
                            <LanguageSwitcher />
                        </div>
                    </div>
                </header>
                <main className="flex-1 p-6">
                    {children}
                </main>
                <footer className="border-t border-slate-200 bg-white px-6 py-3 text-center text-xs text-slate-400">
                    <div>{t("layout.public.footer", { year: new Date().getFullYear() })}</div>
                    <div className="mt-0.5">
                        <a href={t("layout.public.creditUrl")} target="_blank" rel="noopener noreferrer" className="hover:text-slate-600 transition-colors">
                            {t("layout.public.credit")}
                        </a>
                    </div>
                </footer>
            </div>
        </div>
    )
}
